// src/components/students/Students.tsx

import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    TextField,
    InputAdornment,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    Tooltip,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Card,
    CardContent,
    CardActions,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    PersonRemove as PersonRemoveIcon,
    Restore as RestoreIcon,
    Sort as SortIcon,
    DeleteForever as DeleteForeverIcon,
    AccountCircle as AccountIcon,
    AccountCircleOutlined as NoAccountIcon,
    Email as EmailIcon,
} from '@mui/icons-material';
import { useAppContext } from '../../context/AppContext';
import ModalDialog from '../common/ModalDialog';
import StudentForm, { StudentFormRef } from './StudentForm';
import { Student, CreateStudentDto, UpdateStudentDto } from '../../types';
import { Group } from '../../types';
import { AxiosError } from 'axios';
import InvitationDialog from "./InvitationDialog";

type SortField = 'fullName' | 'pricePerLesson' | 'groupName' | 'status';
type SortOrder = 'asc' | 'desc';

// Функция для извлечения сообщения об ошибке из ответа API
const getErrorMessage = (error: unknown): string => {
    if (error && typeof error === 'object' && 'isAxiosError' in error) {
        const axiosError = error as AxiosError;

        if (axiosError.response?.data) {
            const data = axiosError.response.data as any;

            if (data.error && typeof data.error === 'string') {
                return data.error;
            }

            if (data.message && typeof data.message === 'string') {
                return data.message;
            }

            if (data.errors && Array.isArray(data.errors)) {
                return data.errors.join(', ');
            }

            if (typeof data === 'string') {
                return data;
            }
        }

        if (axiosError.response?.status) {
            const status = axiosError.response.status;
            switch (status) {
                case 400:
                    return 'Неверный запрос. Проверьте введенные данные.';
                case 401:
                    return 'Необходима авторизация.';
                case 403:
                    return 'Доступ запрещен.';
                case 404:
                    return 'Запрашиваемый ресурс не найден.';
                case 409:
                    return 'Конфликт данных. Возможно, такой ученик уже существует.';
                case 500:
                    return 'Внутренняя ошибка сервера. Попробуйте позже.';
                default:
                    return `Ошибка сервера (${status}). Попробуйте позже.`;
            }
        }

        if (axiosError.message) {
            return axiosError.message;
        }
    }

    if (error instanceof Error) {
        return error.message;
    }

    if (typeof error === 'string') {
        return error;
    }

    return 'Произошла неизвестная ошибка. Попробуйте позже.';
};

// Функция для естественной сортировки строк с числами
const naturalCompare = (a: string, b: string): number => {
    const aParts = a.match(/(\d+)|(\D+)/g) || [];
    const bParts = b.match(/(\d+)|(\D+)/g) || [];

    let i = 0;
    while (i < aParts.length && i < bParts.length) {
        const aPart = aParts[i];
        const bPart = bParts[i];

        if (/^\d+$/.test(aPart) && /^\d+$/.test(bPart)) {
            const aNum = parseInt(aPart, 10);
            const bNum = parseInt(bPart, 10);
            if (aNum !== bNum) {
                return aNum - bNum;
            }
        } else {
            const result = aPart.localeCompare(bPart);
            if (result !== 0) {
                return result;
            }
        }
        i++;
    }

    return aParts.length - bParts.length;
};

const Students: React.FC = () => {
    const { students, groups, addStudent, updateStudent, activateStudent, deactivateStudent, loading, deleteStudent} = useAppContext();

    // Тема и адаптивность - ЭТИ ХУКИ ДОЛЖНЫ БЫТЬ ВНУТРИ КОМПОНЕНТА
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const formRef = useRef<StudentFormRef>(null);

    const [modalOpen, setModalOpen] = useState(false);
    const [editingStudent, setEditingStudent] = useState<Student | null>(null);
    const [formLoading, setFormLoading] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [filterGroup, setFilterGroup] = useState<number | 'all'>('all');
    const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'inactive'>('all');

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletingStudent, setDeletingStudent] = useState<Student | null>(null);

    // Состояния для сортировки
    const [sortField, setSortField] = useState<SortField>('fullName');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

    // Состояние для диалога подтверждения
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        student: Student | null;
        action: 'activate' | 'deactivate';
    }>({
        open: false,
        student: null,
        action: 'deactivate',
    });

    const [notification, setNotification] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'success',
    });

    const [invitationDialogOpen, setInvitationDialogOpen] = useState(false);
    const [invitationData, setInvitationData] = useState<{
        token: string;
        expiresAt: string;
        studentName: string;
    } | null>(null);

    // Получить имя группы по ID
    const getGroupName = useCallback((groupId: number | null | undefined): string => {
        if (!groupId) return '';
        const group = groups.find(g => g.id === groupId);
        return group ? group.name : '';
    }, [groups]);

    // Фильтрация и сортировка учеников
    const filteredAndSortedStudents = useMemo(() => {
        // Сначала фильтруем
        let result = students.filter(student => {
            const searchMatch =
                student.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                student.phone?.includes(searchTerm);

            const groupMatch =
                filterGroup === 'all' || student.groupId === filterGroup;

            const statusMatch =
                filterStatus === 'all' ||
                (filterStatus === 'active' && student.isActive) ||
                (filterStatus === 'inactive' && !student.isActive);

            return searchMatch && groupMatch && statusMatch;
        }, [students, searchTerm, filterGroup, filterStatus, sortField, sortOrder, getGroupName]);

        // Затем сортируем
        result.sort((a, b) => {
            let comparison = 0;

            switch (sortField) {
                case 'fullName':
                    // Используем естественную сортировку для ФИО
                    comparison = naturalCompare(a.fullName, b.fullName);
                    break;
                case 'pricePerLesson':
                    comparison = a.pricePerLesson - b.pricePerLesson;
                    break;
                case 'groupName': {
                    const aGroup = getGroupName(a.groupId);
                    const bGroup = getGroupName(b.groupId);
                    comparison = naturalCompare(aGroup, bGroup);
                    break;
                }
                case 'status':
                    // Сначала активные, потом неактивные (или наоборот)
                    comparison = (a.isActive === b.isActive) ? 0 : (a.isActive ? -1 : 1);
                    break;
                default:
                    comparison = 0;
            }

            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [students, searchTerm, filterGroup, filterStatus, sortField, sortOrder]);

    const handleOpenCreate = () => {
        setEditingStudent(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (student: Student) => {
        setEditingStudent(student);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setEditingStudent(null);
        setFormLoading(false);
    };

    const { regenerateInvitation } = useAppContext();   // ← добавить в деструктуризацию

    const handleRegenerateInvitation = async (student: Student) => {
        try {
            const invitation = await regenerateInvitation(student.id);
            setInvitationData({
                token: invitation.token,
                expiresAt: invitation.expiresAt,
                studentName: student.fullName,
            });
            setInvitationDialogOpen(true);
            showNotification('Новая ссылка сгенерирована', 'success');
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showNotification(errorMessage, 'error');
        }
    };

    const handleSaveStudent = async (data: CreateStudentDto) => {
        setFormLoading(true);
        try {
            if (editingStudent) {
                await updateStudent(editingStudent.id, data as UpdateStudentDto);
                showNotification('Ученик успешно обновлён', 'success');
                handleCloseModal();
            } else {
                const response = await addStudent(data);

                if (data.isNeedAccount) {
                    showNotification('Ученик создан с учётной записью (пароль: 00000000)', 'success');
                    handleCloseModal();
                } else if (response.invitationToken && response.invitationExpiresAt) {
                    // Показать диалог с ссылкой
                    setInvitationData({
                        token: response.invitationToken,
                        expiresAt: response.invitationExpiresAt,
                        studentName: response.student.fullName,
                    });
                    setInvitationDialogOpen(true);
                    handleCloseModal();
                } else {
                    showNotification('Ученик создан', 'success');
                    handleCloseModal();
                }
            }
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showNotification(errorMessage, 'error');
            setFormLoading(false);
        }
    };

    const handleSaveClick = () => {
        if (formRef.current) {
            formRef.current.submit();
        }
    };

    // Открыть диалог подтверждения для деактивации
    const handleDeactivateClick = (student: Student) => {
        setConfirmDialog({
            open: true,
            student,
            action: 'deactivate',
        });
    };

    // Открыть диалог подтверждения для активации
    const handleActivateClick = (student: Student) => {
        setConfirmDialog({
            open: true,
            student,
            action: 'activate',
        });
    };

    // Подтверждение действия (активация или деактивация)
    const handleConfirmAction = async () => {
        if (!confirmDialog.student) return;

        const { student, action } = confirmDialog;
        const isActivate = action === 'activate';

        try {
            if (isActivate) {
                await activateStudent(student.id);   // ← специальный метод
                showNotification(`Ученик "${student.fullName}" активирован`, 'success');
            } else {
                await deactivateStudent(student.id); // ← специальный метод
                showNotification(`Ученик "${student.fullName}" деактивирован`, 'success');
            }

            setConfirmDialog({ open: false, student: null, action: 'deactivate' });
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showNotification(errorMessage, 'error');
        }
    };

    // Закрыть диалог подтверждения
    const handleCloseConfirmDialog = () => {
        setConfirmDialog({ open: false, student: null, action: 'deactivate' });
    };

    // Обработчик изменения поля сортировки
    const handleSortFieldChange = (event: SelectChangeEvent<SortField>) => {
        setSortField(event.target.value as SortField);
    };

    // Обработчик изменения порядка сортировки
    const handleSortOrderToggle = () => {
        setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const showNotification = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
        setNotification({ open: true, message, severity });
    };

    const getStatusColor = (isActive: boolean) => {
        return isActive ? 'success' : 'error';
    };

    const getStatusText = (isActive: boolean) => {
        return isActive ? 'Активен' : 'Неактивен';
    };

    // Получить текст для отображения направления сортировки
    const getSortOrderLabel = (order: SortOrder) => {
        return order === 'asc' ? '↑' : '↓';
    };

    // Открыть диалог удаления
    const handleOpenDeleteDialog = (student: Student) => {
        setDeletingStudent(student);
        setDeleteDialogOpen(true);
    };

// Подтвердить удаление
    const handleConfirmDelete = async () => {
        if (!deletingStudent) return;

        try {
            await deleteStudent(deletingStudent.id);
            showNotification(
                `Ученик "${deletingStudent.fullName}" полностью удалён`,
                'success'
            );
            setDeleteDialogOpen(false);
            setDeletingStudent(null);
        } catch (error) {
            const errorMessage = getErrorMessage(error);
            showNotification(errorMessage, 'error');
        }
    };

    return (
        <Box>
            {/* Заголовок */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 },
                mb: 3
            }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" sx={{ fontWeight: 500 }}>
                    👨‍🎓 Ученики
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpenCreate}
                    disabled={loading}
                    fullWidth={isMobile}
                >
                    Добавить ученика
                </Button>
            </Box>

            {/* Фильтры и сортировка */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    {/* Поиск */}
                    <TextField
                        placeholder="Поиск по ФИО или телефону..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        size="small"
                        sx={{ flex: 1 }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />

                    {/* Фильтр по группе */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Группа</InputLabel>
                        <Select
                            value={filterGroup}
                            onChange={(e) => setFilterGroup(e.target.value as number | 'all')}
                            label="Группа"
                        >
                            <MenuItem value="all">Все группы</MenuItem>
                            {groups.map((group: Group) => (
                                <MenuItem key={group.id} value={group.id}>
                                    {group.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Фильтр по статусу */}
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Статус</InputLabel>
                        <Select
                            value={filterStatus}
                            onChange={(e) => setFilterStatus(e.target.value as 'all' | 'active' | 'inactive')}
                            label="Статус"
                        >
                            <MenuItem value="all">Все</MenuItem>
                            <MenuItem value="active">Активные</MenuItem>
                            <MenuItem value="inactive">Неактивные</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Сортировка */}
                    <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Сортировать по</InputLabel>
                        <Select
                            value={sortField}
                            onChange={handleSortFieldChange}
                            label="Сортировать по"
                            startAdornment={
                                <InputAdornment position="start">
                                    <SortIcon fontSize="small" />
                                </InputAdornment>
                            }
                        >
                            <MenuItem value="fullName">ФИО</MenuItem>
                            <MenuItem value="pricePerLesson">Цене</MenuItem>
                            <MenuItem value="groupName">Группе</MenuItem>
                            <MenuItem value="status">Статусу</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Кнопка направления сортировки */}
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleSortOrderToggle}
                        sx={{
                            minWidth: '48px',
                            fontSize: '18px',
                            lineHeight: 1,
                            alignSelf: 'center',
                        }}
                    >
                        {getSortOrderLabel(sortOrder)}
                    </Button>
                </Stack>
            </Paper>

            {/* Мобильная версия - карточки */}
            {isMobile ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {filteredAndSortedStudents.length === 0 ? (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                {students.length === 0
                                    ? 'Нет учеников. Создайте первого ученика!'
                                    : 'Ученики не найдены по заданным фильтрам'}
                            </Typography>
                        </Paper>
                    ) : (
                        filteredAndSortedStudents.map((student) => {
                            const group = groups.find((g: Group) => g.id === student.groupId);
                            return (
                                <Card key={student.id}>
                                    <CardContent sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                {student.fullName}
                                            </Typography>
                                            <Chip
                                                label={getStatusText(student.isActive)}
                                                color={getStatusColor(student.isActive)}
                                                size="small"
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                            {student.hasAccount ? (
                                                <Chip
                                                    icon={<AccountIcon />}
                                                    label="Аккаунт"
                                                    size="small"
                                                    color="info"
                                                    variant="outlined"
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<NoAccountIcon />}
                                                    label="Без аккаунта"
                                                    size="small"
                                                    color="default"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            📞 {student.phone}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            💰 {student.pricePerLesson} ₽
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            👥 {group ? group.name : 'Без группы'}
                                        </Typography>
                                    </CardContent>
                                    <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
                                        {!student.hasAccount && student.isActive && (
                                            <Button
                                                size="small"
                                                color="primary"
                                                startIcon={<EmailIcon />}
                                                onClick={() => handleRegenerateInvitation(student)}
                                                disabled={loading}
                                            >
                                                Ссылка
                                            </Button>
                                        )}
                                        <Button
                                            size="small"
                                            startIcon={<EditIcon />}
                                            onClick={() => handleOpenEdit(student)}
                                            disabled={loading}
                                        >
                                            Изменить
                                        </Button>

                                        {student.isActive ? (
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<PersonRemoveIcon />}
                                                onClick={() => handleDeactivateClick(student)}
                                                disabled={loading}
                                            >
                                                Деактивировать
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    size="small"
                                                    color="success"
                                                    startIcon={<RestoreIcon />}
                                                    onClick={() => handleActivateClick(student)}
                                                    disabled={loading}
                                                >
                                                    Восстановить
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    startIcon={<DeleteForeverIcon />}
                                                    onClick={() => handleOpenDeleteDialog(student)}
                                                    disabled={loading}
                                                >
                                                    Удалить
                                                </Button>
                                            </>
                                        )}
                                    </CardActions>
                                </Card>
                            );
                        })
                    )}
                </Box>
            ) : (
                /* Десктопная версия - таблица */
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">ФИО</TableCell>
                                <TableCell align="center">Телефон</TableCell>
                                <TableCell align="center">Цена</TableCell>
                                <TableCell align="center">Группа</TableCell>
                                <TableCell align="center">Статус</TableCell>
                                <TableCell align="center">Действия</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredAndSortedStudents.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography color="text.secondary" sx={{ py: 3 }}>
                                            {students.length === 0
                                                ? 'Нет учеников. Создайте первого ученика!'
                                                : 'Ученики не найдены по заданным фильтрам'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredAndSortedStudents.map((student) => {
                                    const group = groups.find((g: Group) => g.id === student.groupId);
                                    return (
                                        <TableRow key={student.id} hover>
                                            <TableCell align="center">{student.fullName}</TableCell>
                                            <TableCell align="center">{student.phone}</TableCell>
                                            <TableCell align="center">{student.pricePerLesson} ₽</TableCell>
                                            <TableCell align="center">
                                                {group ? group.name : '—'}
                                            </TableCell>
                                            <TableCell align="center">
                                                {/*<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }} >*/}
                                                    <Chip
                                                        label={getStatusText(student.isActive)}
                                                        color={getStatusColor(student.isActive)}
                                                        size="small"
                                                        sx={{ mr: 1 }}
                                                    />
                                                    {student.hasAccount ? (
                                                        <Tooltip title="Есть учётная запись">
                                                            <Chip
                                                                icon={<AccountIcon />}
                                                                label="+"
                                                                size="small"
                                                                color="info"
                                                                variant="outlined"
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Учётная запись не создана">
                                                            <Chip
                                                                icon={<NoAccountIcon />}
                                                                label="-"
                                                                size="small"
                                                                color="default"
                                                                variant="outlined"
                                                            />
                                                        </Tooltip>
                                                    )}
                                                {/*</Box>*/}
                                            </TableCell>
                                            <TableCell align="center">
                                                {!student.hasAccount && student.isActive && (
                                                    <Tooltip title="Сгенерировать ссылку-приглашение">
                                                        <IconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleRegenerateInvitation(student)}
                                                            disabled={loading}
                                                        >
                                                            <EmailIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Редактировать">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenEdit(student)}
                                                        disabled={loading}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>

                                                {student.isActive ? (
                                                    <Tooltip title="Деактивировать">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeactivateClick(student)}
                                                            disabled={loading}
                                                        >
                                                            <PersonRemoveIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                ) : (
                                                    <>
                                                        <Tooltip title="Восстановить">
                                                            <IconButton
                                                                size="small"
                                                                color="success"
                                                                onClick={() => handleActivateClick(student)}
                                                                disabled={loading}
                                                            >
                                                                <RestoreIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Удалить навсегда">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleOpenDeleteDialog(student)}
                                                                disabled={loading}
                                                            >
                                                                <DeleteForeverIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <ModalDialog
                open={modalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveClick}
                title={editingStudent ? 'Редактирование ученика' : 'Добавление ученика'}
                loading={formLoading}
                disabled={formLoading}
            >
                <StudentForm
                    ref={formRef}
                    initialData={editingStudent}
                    groups={groups}
                    onSubmit={handleSaveStudent}
                    loading={formLoading}
                />
            </ModalDialog>

            {/* Диалог подтверждения */}
            <Dialog
                open={confirmDialog.open}
                onClose={handleCloseConfirmDialog}
            >
                <DialogTitle>
                    {confirmDialog.action === 'activate' ? 'Активация ученика' : 'Деактивация ученика'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmDialog.action === 'activate'
                            ? `Вы уверены, что хотите активировать ученика "${confirmDialog.student?.fullName}"? 
                 Он сможет снова участвовать в уроках и быть добавлен в группы.`
                            : `Вы уверены, что хотите деактивировать ученика "${confirmDialog.student?.fullName}"? 
                 Ученик будет удален из всех групп, а его будущие уроки будут отменены. 
                 История уроков сохранится.`
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmDialog}>Отмена</Button>
                    <Button
                        onClick={handleConfirmAction}
                        color={confirmDialog.action === 'activate' ? 'success' : 'error'}
                        variant="contained"
                    >
                        {confirmDialog.action === 'activate' ? 'Активировать' : 'Деактивировать'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Диалог удаления ученика */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => {
                    setDeleteDialogOpen(false);
                    setDeletingStudent(null);
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeleteForeverIcon color="error" />
                        <Typography variant="h6" component="span" sx={{ fontWeight: 600, color: 'error.main' }}>
                            Удаление ученика навсегда
                        </Typography>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {deletingStudent && (
                        <>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                                    ⚠️ Это действие нельзя отменить!
                                </Typography>
                                <Typography variant="body2">
                                    Будет полностью удалён ученик, его учётная запись и <strong>все его уроки</strong> —
                                    как прошедшие, так и будущие.
                                </Typography>
                            </Alert>

                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Статистика станет неточной
                                </Typography>
                                <Typography variant="body2">
                                    Все данные об уроках этого ученика исчезнут из отчётов за прошлые месяцы.
                                </Typography>
                            </Alert>

                            {/* Информация об ученике */}
                            <Box sx={{
                                p: 2,
                                bgcolor: 'action.hover',
                                borderRadius: 1,
                                borderLeft: '4px solid',
                                borderColor: 'error.main',
                                mb: 2,
                            }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {deletingStudent.fullName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    📞 {deletingStudent.phone || deletingStudent.phone}
                                </Typography>
                                {deletingStudent.email && (
                                    <Typography variant="body2" color="text.secondary">
                                        ✉️ {deletingStudent.email}
                                    </Typography>
                                )}
                            </Box>

                            <Alert severity="info">
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    💡 Рекомендуем деактивировать
                                </Typography>
                                <Typography variant="body2">
                                    Если вы хотите сохранить статистику за прошлые месяцы —
                                    лучше деактивировать ученика. Он не будет появляться в активных списках,
                                    но все данные о нём сохранятся.
                                </Typography>
                            </Alert>
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Button
                        onClick={() => {
                            setDeleteDialogOpen(false);
                            setDeletingStudent(null);
                        }}
                    >
                        Отмена
                    </Button>

                    {/* Кнопка деактивации, если ученик ещё активен */}
                    {deletingStudent?.isActive && (
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<PersonRemoveIcon />}
                            onClick={() => {
                                setDeleteDialogOpen(false);
                                handleDeactivateClick(deletingStudent);
                            }}
                        >
                            Деактивировать
                        </Button>
                    )}

                    {/* Кнопка удаления */}
                    <Button
                        variant="contained"
                        color="error"
                        startIcon={<DeleteForeverIcon />}
                        onClick={handleConfirmDelete}
                    >
                        Всё равно удалить
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={notification.open}
                autoHideDuration={5000}
                onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setNotification(prev => ({ ...prev, open: false }))}
                    severity={notification.severity}
                    variant="filled"
                >
                    {notification.message}
                </Alert>
            </Snackbar>
            {invitationData && (
                <InvitationDialog
                    open={invitationDialogOpen}
                    onClose={() => {
                        setInvitationDialogOpen(false);
                        setInvitationData(null);
                    }}
                    token={invitationData.token}
                    expiresAt={invitationData.expiresAt}
                    studentName={invitationData.studentName}
                />
            )}
        </Box>
    );
};

export default Students;